import { Upload } from "lucide-react";
import { useState } from "react";
import { LoadingButton } from "../../../components/button/loading-button";
import { FileUpload, type PersistedUploadFile } from "../../../components/file-upload";
import { useToast } from "../../../contexts/toast/toast-context";
import api from "../../../services/api";
import type { User, UserAttachment } from "../../../types/user";
import { getApiErrorMessage } from "../../../utils";

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

type ClientAttachmentsSectionProps = {
  userId: string;
  attachments?: UserAttachment[];
  onUserChange: (user: User) => void;
};

export function ClientAttachmentsSection({
  userId,
  attachments = [],
  onUserChange,
}: ClientAttachmentsSectionProps) {
  const { error, success } = useToast();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);

  const uploadFiles = async () => {
    if (selectedFiles.length === 0 || isUploading) return;

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append("files", file));

    setIsUploading(true);

    try {
      const response = await api.post<User>(
        `/user/${userId}/attachments`,
        formData,
      );
      onUserChange(response.data);
      setSelectedFiles([]);
      success("Anexo adicionado com sucesso.");
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const deleteAttachment = async (attachment: UserAttachment) => {
    if (deletingKey) return;

    setDeletingKey(attachment.key);

    try {
      const response = await api.delete<User>(`/user/${userId}/attachments`, {
        data: { key: attachment.key },
      });
      onUserChange(response.data);
      success("Anexo removido com sucesso.");
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    } finally {
      setDeletingKey(null);
    }
  };

  return (
    <section className="app-card p-6">
      <div className="mb-4">
        <div>
          <h2 className="app-section-title">Anexos</h2>
          <p className="mt-1 text-sm text-slate-500">
            Arquivos do contato com limite de 5 MB por arquivo.
          </p>
        </div>
      </div>
      <FileUpload files={selectedFiles} onFilesChange={setSelectedFiles} maxFiles={10} maxSizeBytes={MAX_FILE_SIZE_BYTES} acceptLabel="PDF, imagens, documentos e outros" persistedFiles={attachments.map((attachment) => ({ id: attachment.key, name: attachment.fileName, size: attachment.size, type: attachment.contentType, url: attachment.url, date: attachment.createdAt }))} onRemovePersisted={(file: PersistedUploadFile) => { const attachment = attachments.find((item) => item.key === file.id); if (attachment && window.confirm(`Excluir o anexo "${attachment.fileName}"?`)) void deleteAttachment(attachment); }} removingPersistedId={deletingKey} uploading={isUploading} onValidationError={error} />
        <div className="mt-4 flex justify-end">
          <LoadingButton
            type="button"
            onClick={uploadFiles}
            isLoading={isUploading}
            loadingLabel="Enviando"
            disabled={selectedFiles.length === 0 || isUploading}
            className="btn btn-neutral cursor-pointer"
          >
            <Upload className="w-4 h-4" />
            Enviar
          </LoadingButton>
        </div>
    </section>
  );
}
